import React, {useEffect, useRef, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import _cloneDeep from 'lodash/cloneDeep';
import ManualDataTable from '../../../../../components/manualdatatable/ManualDatatable';
import {
  DateRangeFilter,
  TextFilter,
} from '../../../../../components/manualdatatable/ManualDataFilters';
import FormattedDate from '../../../../../components/product/FormattedDate';
import ActionButton from '../../../../../components/datatable/ActionButton';
import skincareProDao from '../../../../../dao/skincareProDao';
import AddSkincare from '../../addskincarehead';
import _get from 'lodash/get';
import './index.css';
import ViewProductModal from './ViewProductModal';
import InfoDialog from '../../../../../components/dialogs/InfoDialog';
import utils from '../../../../../utils/smbUtils';
import smbUtils from '../../../../../utils/smbUtils';
import {history} from '../../../../../utils/redux/store';
import {multipleSubscriptionsUtils} from '../../../../../utils/solutionUtils';
import {privilegeUtils} from '../../../../../utils/privilegeUtils';

const ControlBar = ({isQuotaFull = false}) => {
  const {t} = useTranslation();
  return (
    <div className="control-bar">
      <ActionButton
        type={'customize'}
        disabled={isQuotaFull}
        disableDesc={
          <Trans i18nKey="p.s.listskincareproduct.add.skincareproduct.quota.full">
            Cannot add more{' '}
            {{product: t('p.s.listskincareroutine.routine', 'routine')}} as the
            total quota has been reached.
          </Trans>
        }
        customize={{
          text: (
            <>
              <i className="fa fa-plus" />
              <Trans i18nKey="p.s.listskincareroutine.add.skincare.routine">
                Add Skincare Routine
              </Trans>
            </>
          ),
        }}
        onClick={() =>
          history.push('/skincare-pro/add-skincare/add-skincare-routine')
        }
      />
    </div>
  );
};

export default function SkincareRoutine() {
  const accountSummary = useSelector(
    state => state.components.auth.accountSummary
  );
  const {token, customerId} = utils.customerInfo(accountSummary);
  const {SKIN_ANALYSIS} = multipleSubscriptionsUtils.getSolutionInfos(
    accountSummary
  );
  const productCountTotal = _get(SKIN_ANALYSIS, 'maxNumOfProducts', 0);
  const [isLoading, setIsLoading] = useState(false);
  const [formattedData, setFormattedData] = useState({
    results: [],
    totalSize: 0,
  });
  const [productCount, setProductCount] = useState(0);
  const [processedParams, setProcessedParams] = useState(null);
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const isQuotaFull =
    productCountTotal === -1 ? false : productCount >= productCountTotal;
  let callPaginationAPI = useRef(null);

  const listRoutineAPI = async () => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append(
      'types',
      JSON.stringify([{type1: 'Store', type2: 'SMB', type3: 'SMB_ROUTINE'}])
    );
    tmp.append('service', 'INVENTORY');

    const apiFormattedParams = _cloneDeep(processedParams);
    tmp.append('limit', apiFormattedParams.pageSize);
    tmp.append(
      'offset',
      (apiFormattedParams.pageIndex - 1) * apiFormattedParams.pageSize
    );
    delete apiFormattedParams.pageSize;
    delete apiFormattedParams.pageIndex;

    for (const [key, value] of Object.entries(apiFormattedParams)) {
      if (key === 'status' && value === 'Offline') {
        tmp.append(key, JSON.stringify(['Active', 'Offline']));
        continue;
      }
      if (
        key === 'createdTimeFrom' ||
        key === 'createdTimeTo' ||
        key === 'lastModifiedFrom' ||
        key === 'lastModifiedTo'
      ) {
        tmp.append(key, new Date(value).toISOString());
        continue;
      }
      tmp.append(key, value);
    }

    await skincareProDao
      .getGenericList(tmp)
      .then(res => {
        if (res.results) {
          const newRes = res.results.map(item => {
            return {
              id: item.id,
              deletable: item.deletable,
              status: item.editingRevision.status,
              createdTime: item.createdTime,
              lastModified: item.lastModified,
              name: item.editingRevision.name,
              i18n: item.editingRevision.i18n,
            };
          });
          setFormattedData({
            ...formattedData,
            results: newRes,
            totalSize: _get(res, 'totalSize', 0),
          });
          setIsLoading(false);
          return;
        }
      })
      .catch(err => {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
      });
    setIsLoading(false);
  };
  const getProductCountAPI = async () => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    await skincareProDao.getProductCount(tmp).then(res => {
      if (res.status === 'success' && res.count) {
        setProductCount(res.count);
      }
    });
  };
  const copyRoutineAPI = async productId => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('productId', productId);
    const res = await skincareProDao.copyGenericList(tmp);
    if (!res || res.status !== 'success') {
      toast.error(
        <Trans i18nKey="p.s.addoreditcategory.error.failed">Failed</Trans>
      );
      return;
    }
    await getProductCountAPI();
    await listRoutineAPI();
    toast.success(<Trans i18nKey="toast.success.copy.product">Copied!</Trans>);
    setIsLoading(false);
  };
  const deleteRoutineAPI = async (productId, productName) => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('productId', productId);
    const res = await skincareProDao.deleteGenericList(tmp);
    if (!res || res.status !== 'success') {
      toast.error(
        <Trans i18nKey="toast.error.delete.product">
          Failed to delete {{productName}}.
        </Trans>
      );
      return;
    }
    await getProductCountAPI();
    await listRoutineAPI();
    toast.success(
      <Trans i18nKey="toast.success.delete.product">Delete!</Trans>
    );
    setIsLoading(false);
  };

  useEffect(() => {
    getProductCountAPI();
  }, []);

  useEffect(() => {
    if (!processedParams) {
      return;
    }
    setIsLoading(true);
    callPaginationAPI.current = smbUtils.clearPrevAPI(
      callPaginationAPI.current,
      listRoutineAPI,
      500
    );
  }, [processedParams]);

  const handleCloseModel = async () => {
    setEditId('');
    setShowModal(false);
    setIsLoading(true);
  };

  const columns = [
    {
      Header: (
        <InfoDialog
          trigger={'click'}
          placement="right"
          title={
            <>
              <i className="fa fa-question-circle" />{' '}
              <Trans i18nKey="p.s.listskincareroutine.routine.Name">
                Routine Name
              </Trans>
            </>
          }
          description={
            <Trans i18nKey="p.s.listskincareroutine.routine.Name.desc">
              Names are displayed in default language.
            </Trans>
          }
        >
          <label className="cursor-help">
            <Trans i18nKey="p.s.listskincareroutine.routine.Name">
              Routine Name
            </Trans>{' '}
            <i className="fa fa-question-circle" />
          </label>
        </InfoDialog>
      ),
      accessor: 'name',
      style: {width: '20%', textAlign: 'center', wordBreak: 'break-word'},
      disableSortBy: true,
      Filter: TextFilter,
      Cell: ({
        cell: {
          row: {original},
        },
      }) => (
        <a
          style={{color: '#5867dd', cursor: 'pointer'}}
          onClick={() => {
            setEditId(original.id);
            setShowModal(true);
          }}
        >
          {original.name}
        </a>
      ),
    },
    {
      Header: (
        <Trans i18nKey="p.s.listskincareroutine.routine.id">Routine ID</Trans>
      ),
      accessor: 'id',
      disableSortBy: true,
      style: {width: '10%', textAlign: 'center'},
      Filter: TextFilter,
    },
    {
      Header: (
        <Trans i18nKey="p.s.listskincareroutine.creation">Creation</Trans>
      ),
      accessor: 'createdTime',
      Filter: DateRangeFilter,
      style: {width: '20%', textAlign: 'center'},
      Cell: ({cell: {value}}) => (
        <div className="text-right">
          <FormattedDate time={value} type={'long'} />
        </div>
      ),
    },
    {
      Header: (
        <Trans i18nKey="p.s.listskincareroutine.modification">
          Modification
        </Trans>
      ),
      accessor: 'lastModified',
      Filter: DateRangeFilter,
      style: {width: '20%', textAlign: 'center'},
      Cell: ({cell: {value}}) => (
        <div className="text-right">
          <FormattedDate time={value} type={'long'} />
        </div>
      ),
    },
    {
      Header: <Trans i18nKey="p.s.listskincareroutine.actions">Actions</Trans>,
      accessor: 'id',
      disableSortBy: true,
      id: 'actions',
      style: {width: '20%', textAlign: 'center'},
      Cell: ({
        cell: {
          row: {original},
        },
      }) => (
        <div
          className="add-skincare-product-list-page-action-button"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ActionButton
            type={'edit'}
            url={'/skincare-pro/add-skincare/edit-skincare-routine'}
            query={{productId: original.id}}
          />
          <ActionButton
            type={'copy'}
            onClick={async () => {
              await copyRoutineAPI(original.id);
            }}
            disabled={isQuotaFull}
            disableDesc={
              <Trans i18nKey="p.s.listskincareroutine.disable.copy.desc">
                The Routine cannot be duplicated as the maximum amount has been
                reached. Please upgrade your plan or remove existing product to
                proceed.
              </Trans>
            }
          />
          <ActionButton
            type={'delete'}
            onClick={async () => {
              await deleteRoutineAPI(original.id, original.name);
            }}
            disabled={!original.deletable}
            disableDesc={
              <Trans i18nKey="p.s.listskincareroutine.disable.delete.desc">
                This Routine is being used in Recommendation Cases. If you wish
                to delete this routine, please remove it from Recommendation
                Cases first.
              </Trans>
            }
            confirmTitle={
              <Trans i18nKey="p.s.listskincareroutine.confirm.delete.desc">
                After deletion, the routine will no longer be available on past
                client reports. Are you sure to delete?
              </Trans>
            }
          />
        </div>
      ),
    },
  ];

  if (
    !(
      privilegeUtils.canManageSku(accountSummary) ||
      privilegeUtils.isTechnicalSupport(accountSummary)
    )
  ) {
    history.push('/');
  }

  return (
    <>
      <AddSkincare
        numOfEnabled={productCount}
        numOfAvailable={productCountTotal}
      />
      <div className="portlet-body">
        <ControlBar isQuotaFull={isQuotaFull} />
        <div className="add-skincare-description">
          <img
            alt="guid"
            src={require('../../../../../assets/images/help/help_start_guide.svg')}
            className="kt-margin-b-5 kt-margin-r-5"
          />{' '}
          <Trans i18nKey="p.s.listskincareroutine.desc">
            Skincare Routines consist of steps and skincare products you take
            for your skin regularly.
          </Trans>
        </div>
        <div className="list-skincare-routine-page">
          <ManualDataTable
            isLoading={isLoading}
            columns={columns}
            data={formattedData.results}
            totalSize={formattedData.totalSize}
            initState={{
              sortBy: {id: 'lastModified', desc: true},
              pageSize: 10,
              pageIndex: 1,
            }}
            setProcessedParams={setProcessedParams}
            isScrollX={true}
          />
          <ViewProductModal
            editId={editId}
            showModal={showModal}
            handleCloseModel={handleCloseModel}
          />
        </div>
      </div>
    </>
  );
}
